// Settings → Key management (S-2, api-contract.md §5): every service or person that calls the Hub is a consumer with
// its own keys, the APIs it may call (scopes) and its limits. A key is shown once, when made; the Hub keeps its hash.
// Replacing a key without a gap: make the new one, put it where it goes, then revoke the old one. On the fixtures
// (npm test) the tab only says what it does.
import { useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Badge, Can, ConfirmDialog, Drawer, KeysShownOnce, Switch, formatDate, formatDateTime, formatRelative, notify } from '@/kit/ui'
import { apiMode } from '@/lib/hubApi'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { SCOPES, addConsumer, deleteConsumer, getConsumers, makeKey, nameFrom, revokeKey, scopeLabel, updateConsumer, type Consumer, type ConsumerKey, type ConsumerKeyMade, type RateLimit, type Scope } from '@/lib/keysApi'

/** Where a new key goes, said plainly: the consumer keeps its old key until that one is revoked. */
function whereItGoes(c: Consumer) {
  if (c.name === 'app') return 'Put it in firstfund-app/.env as HUB_APP_KEY and restart app-api and its worker (docker compose up -d api worker in firstfund-app). When the app works with it, revoke the old key here.'
  if (c.name === 'website') return 'Give it to whoever sets up the website: its x-app-key for POST /v1/applications.'
  if (c.name === 'developers') return "Postman: put it in the environment's hubKey (sent as x-hub-key). Share it only with people who should call the lenders' endpoints."
  const headers = [...new Set(SCOPES.filter((s) => c.scopes.includes(s.id)).map((s) => s.header))]
  return `Give it to ${c.title}; it goes in the ${headers.join(' / ') || 'right'} header.`
}

const ENDS = [{ value: '', label: 'No end date' }, { value: '30', label: 'In 30 days' }, { value: '90', label: 'In 90 days' }, { value: '365', label: 'In 1 year' }]
const STATE_TONE = { active: 'success', expired: 'warning', revoked: 'gray' } as const

export function KeyManagement() {
  const [list, setList] = useState<Consumer[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const load = () => getConsumers().then((l) => { setList(l); setError(null) }, (e: Error) => setError(e.message))
  useEffect(() => { if (apiMode()) void load() }, [])

  if (!apiMode()) {
    return <p className="text-xs text-muted">Each service that calls the Hub (the web app, the website, developers with Postman) is a consumer with its own keys, the APIs it may call and its limits. This needs the Hub API.</p>
  }
  const put = (c: Consumer) => setList((l) => (l ?? []).map((x) => (x.name === c.name ? c : x)))
  return (
    <div className="space-y-3" data-testid="key-management">
      {error && <p className="field-error">{error}</p>}
      {!list && !error && <p className="text-2xs text-muted">Reading the consumers…</p>}
      <div className="flex items-center justify-between gap-2">
        <p className="text-2xs text-muted">A consumer = one service or person that calls the Hub. Its key says who it is; its scopes say what it may call; its limits say how often.</p>
        <Can permission="settings.manage">
          <button type="button" className="btn btn-secondary btn-sm shrink-0" onClick={() => setAdding(true)} data-testid="add-consumer"><Plus size={14} /> Add consumer</button>
        </Can>
      </div>
      {list?.map((c) => <ConsumerCard key={c.name} consumer={c} onChanged={put} onDeleted={() => setList((l) => (l ?? []).filter((x) => x.name !== c.name))} />)}
      <AddConsumer isOpen={adding} onClose={() => setAdding(false)} taken={(list ?? []).map((c) => c.name)} onAdded={(c) => { setList((l) => [...(l ?? []), c]); setAdding(false) }} />
    </div>
  )
}

function ConsumerCard({ consumer: c, onChanged, onDeleted }: { consumer: Consumer; onChanged: (c: Consumer) => void; onDeleted: () => void }) {
  const { can } = useCurrentUser()
  const editable = can('settings.manage')
  const [scopes, setScopes] = useState<Scope[]>(c.scopes)
  const [limits, setLimits] = useState<RateLimit[]>(c.limits)
  const [busy, setBusy] = useState(false)
  const [newKey, setNewKey] = useState<{ label: string; ends: string } | null>(null)
  const [made, setMade] = useState<ConsumerKeyMade | null>(null)
  const [asking, setAsking] = useState<null | { kind: 'revoke'; key: ConsumerKey } | { kind: 'delete' } | { kind: 'off' }>(null)
  useEffect(() => { setScopes(c.scopes); setLimits(c.limits) }, [c])

  const scopesDirty = scopes.join() !== c.scopes.join()
  const limitsDirty = JSON.stringify(limits) !== JSON.stringify(c.limits)
  const working = c.keys.filter((k) => k.state === 'active')

  async function run<T>(what: string, call: () => Promise<T>, done: (r: T) => void) {
    setBusy(true)
    try { done(await call()) } catch (e) { notify.error(what, (e as Error).message) } finally { setBusy(false); setAsking(null) }
  }
  const save = (body: Parameters<typeof updateConsumer>[1], said: string) => run('Not saved', () => updateConsumer(c.name, body), (r) => { onChanged(r); notify.success(`${c.title}: ${said}`) })
  const setLimit = (scope: Scope, k: keyof Omit<RateLimit, 'scope'>, v: string) => setLimits((ls) => ls.map((l) => (l.scope === scope ? { ...l, [k]: k === 'perDay' && v.trim() === '' ? null : Math.max(1, Math.round(Number(v) || 1)) } : l)))

  return (
    <section className="card space-y-3" data-testid={`consumer-${c.name}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            {c.title} <span className="mono text-2xs font-normal text-muted">{c.name}</span>
            {c.builtin && <Badge tone="gray">built in</Badge>}
            {!c.active && <Badge tone="danger">off</Badge>}
          </h3>
          {c.purpose && <p className="text-2xs text-muted">{c.purpose}</p>}
        </div>
        <div className="flex items-center gap-3">
          <Switch label={c.active ? 'On' : 'Off'} checked={c.active} disabled={!editable || busy} onChange={(on) => (on ? void save({ active: true }, 'switched on') : setAsking({ kind: 'off' }))} />
          {!c.builtin && editable && <button type="button" className="btn btn-ghost btn-sm" aria-label={`Delete ${c.title}`} onClick={() => setAsking({ kind: 'delete' })}><Trash2 size={14} /></button>}
        </div>
      </div>

      <div>
        <p className="label">May call</p>
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
          {SCOPES.map((s) => (
            <label key={s.id} className="flex items-start gap-2 text-xs">
              <input type="checkbox" className="mt-0.5" disabled={!editable} checked={scopes.includes(s.id)} onChange={(e) => setScopes((x) => SCOPES.map((y) => y.id).filter((id) => (id === s.id ? e.target.checked : x.includes(id))))} data-testid={`scope-${c.name}-${s.id}`} />
              <span>{s.label}<span className="block text-2xs text-muted">{s.apis} · {s.header}</span></span>
            </label>
          ))}
        </div>
        {scopesDirty && editable && <button type="button" className="btn btn-primary btn-sm mt-2" disabled={busy} onClick={() => void save({ scopes }, 'scopes saved')} data-testid={`save-scopes-${c.name}`}>Save scopes</button>}
      </div>

      {limits.length > 0 && (
        <div>
          <p className="label">Limits <span className="font-normal text-muted">(over them: 429 with Retry-After)</span></p>
          <div className="overflow-x-auto">
            <table className="table">
              <thead><tr><th>Scope</th><th>Per minute</th><th>Burst</th><th>Per day <span className="font-normal text-muted">(empty = none)</span></th></tr></thead>
              <tbody>
                {limits.map((l) => (
                  <tr key={l.scope}>
                    <td>{scopeLabel(l.scope)}</td>
                    <td><input className="input w-24" inputMode="numeric" aria-label={`${l.scope} per minute`} disabled={!editable} value={l.perMinute} onChange={(e) => setLimit(l.scope, 'perMinute', e.target.value)} /></td>
                    <td><input className="input w-24" inputMode="numeric" aria-label={`${l.scope} burst`} disabled={!editable} value={l.burst} onChange={(e) => setLimit(l.scope, 'burst', e.target.value)} /></td>
                    <td><input className="input w-28" inputMode="numeric" aria-label={`${l.scope} per day`} disabled={!editable} value={l.perDay ?? ''} placeholder="none" onChange={(e) => setLimit(l.scope, 'perDay', e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-2xs text-muted">Per minute = the steady rate; burst = calls at once before that rate applies; per day = a quota for the India day (an Alert at 80%).</p>
          {limitsDirty && editable && <button type="button" className="btn btn-primary btn-sm mt-2" disabled={busy} onClick={() => void save({ limits }, 'limits saved')} data-testid={`save-limits-${c.name}`}>Save limits</button>}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between">
          <p className="label !mb-0">Keys <span className="font-normal text-muted">({working.length} working)</span></p>
          {editable && !newKey && <button type="button" className="btn btn-secondary btn-sm" onClick={() => setNewKey({ label: '', ends: '' })} data-testid={`new-key-${c.name}`}><Plus size={14} /> New key</button>}
        </div>
        {newKey && (
          <div className="mt-2 flex flex-wrap items-end gap-2 rounded-md border border-slate-200 p-2 dark:border-slate-700">
            <div><label className="label" htmlFor={`key-label-${c.name}`}>What it is for</label><input id={`key-label-${c.name}`} className="input w-56" maxLength={80} placeholder="e.g. app server, Ravi's Postman" value={newKey.label} onChange={(e) => setNewKey({ ...newKey, label: e.target.value })} /></div>
            <div><label className="label" htmlFor={`key-ends-${c.name}`}>Ends</label><select id={`key-ends-${c.name}`} className="input" value={newKey.ends} onChange={(e) => setNewKey({ ...newKey, ends: e.target.value })}>{ENDS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy} data-testid={`make-key-${c.name}`} onClick={() => void run('Key not made', () => makeKey(c.name, { label: newKey.label.trim(), expiresAt: newKey.ends ? new Date(Date.now() + Number(newKey.ends) * 86400e3).toISOString() : null }), (r) => {
              setMade(r)
              setNewKey(null)
              // the list keeps the masked key only; the key itself stays in the shown-once box
              const { key: _key, consumer: _consumer, ...masked } = r
              onChanged({ ...c, keys: [masked, ...c.keys] })
              notify.success(`${c.title}: new key made`, 'Copy it now: it is shown only this once.')
            })}>Make the key</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setNewKey(null)}>Cancel</button>
          </div>
        )}
        {made && (
          <div className="mt-2" data-testid="key-shown-once">
            <KeysShownOnce title={`${c.title} key · shown once`} secrets={{ [made.label || 'Key']: made.key }} note={`Copy it now; the Hub keeps only its last 4 characters. ${whereItGoes(c)}`} />
            <button type="button" className="btn btn-secondary btn-sm mt-1" onClick={() => setMade(null)}>I have copied it</button>
          </div>
        )}
        {c.keys.length === 0 ? (
          <p className="mt-1 text-2xs text-muted">No keys yet: nothing can call as {c.title}.</p>
        ) : (
          <div className="mt-1 overflow-x-auto">
            <table className="table">
              <thead><tr><th>Key</th><th>For</th><th>State</th><th>Made</th><th>Ends</th><th>Last used</th><th /></tr></thead>
              <tbody>
                {c.keys.map((k) => (
                  <tr key={k.id} data-testid={`key-${k.id}`} className={k.state === 'active' ? '' : 'opacity-60'}>
                    <td className="mono">{k.hint}</td>
                    <td className="text-2xs">{k.label || <span className="text-muted">—</span>}</td>
                    <td><Badge tone={STATE_TONE[k.state]}>{k.state}</Badge></td>
                    <td className="text-2xs">{formatDateTime(k.createdAt)}<span className="block text-muted">{k.createdBy}</span></td>
                    <td className="text-2xs">{k.revokedAt ? `revoked ${formatDate(k.revokedAt)}` : k.expiresAt ? formatDate(k.expiresAt) : 'no end'}</td>
                    <td className="text-2xs">{k.lastUsedAt ? formatRelative(k.lastUsedAt) : 'never'}</td>
                    <td className="text-right">{editable && k.state === 'active' && <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setAsking({ kind: 'revoke', key: k })} data-testid={`revoke-${k.id}`}>Revoke</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={asking !== null}
        onClose={() => setAsking(null)}
        busy={busy}
        type="danger"
        title={asking?.kind === 'revoke' ? `Revoke key ${asking.key.hint}?` : asking?.kind === 'delete' ? `Delete ${c.title}?` : `Switch ${c.title} off?`}
        message={asking?.kind === 'revoke'
          ? `It stops working at once.${working.length === 1 ? ` It is the last working key: ${c.title} can call nothing until it has a new one.` : ''}`
          : asking?.kind === 'delete' ? `Its ${c.keys.length} key(s) and limits go. Its usage stays in the counts.` : `Every key of ${c.title} answers 401 until it is switched on again.`}
        confirmText={asking?.kind === 'revoke' ? 'Revoke the key' : asking?.kind === 'delete' ? 'Delete' : 'Switch off'}
        onConfirm={() => {
          if (asking?.kind === 'revoke') void run('Key not revoked', () => revokeKey(c.name, asking.key.id), (k) => { onChanged({ ...c, keys: c.keys.map((x) => (x.id === k.id ? k : x)) }); notify.success(`Key ${k.hint} revoked`) })
          else if (asking?.kind === 'delete') void run('Not deleted', () => deleteConsumer(c.name), () => { onDeleted(); notify.success(`${c.title} deleted`) })
          else void save({ active: false }, 'switched off')
        }}
      />
    </section>
  )
}

function AddConsumer({ isOpen, onClose, onAdded, taken }: { isOpen: boolean; onClose: () => void; onAdded: (c: Consumer) => void; taken: string[] }) {
  const [title, setTitle] = useState('')
  const [name, setName] = useState('')
  const [purpose, setPurpose] = useState('')
  const [scopes, setScopes] = useState<Scope[]>([])
  const [busy, setBusy] = useState(false)
  useEffect(() => { if (isOpen) { setTitle(''); setName(''); setPurpose(''); setScopes([]) } }, [isOpen])
  const slug = name || nameFrom(title)
  const problem = !title.trim() ? 'Give it a name people know.' : !/^[a-z][a-z0-9-]{1,39}$/.test(slug) ? 'The short name: lower-case letters, digits and -, starting with a letter.' : taken.includes(slug) ? `There is already a consumer ${slug}.` : null
  const add = async () => {
    setBusy(true)
    try {
      const c = await addConsumer({ name: slug, title: title.trim(), purpose: purpose.trim(), scopes })
      notify.success(`${c.title} added`, 'Make its first key on its card.')
      onAdded(c)
    } catch (e) {
      notify.error('Not added', (e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Add a consumer" subtitle="A service or person that calls the Hub with its own keys" size="md"
      footer={<div className="flex justify-end gap-2"><button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" disabled={!!problem || busy} onClick={() => void add()} data-testid="add-consumer-save">Add</button></div>}>
      <div className="space-y-3">
        <div><label className="label" htmlFor="consumer-title">Name</label><input id="consumer-title" className="input" maxLength={80} placeholder="e.g. Partner bank" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
        <div><label className="label" htmlFor="consumer-name">Short name</label><input id="consumer-name" className="input mono" maxLength={40} placeholder={nameFrom(title) || 'partner-bank'} value={name} onChange={(e) => setName(e.target.value.toLowerCase())} /><p className="text-2xs text-muted">In the usage and the logs; cannot change later.</p></div>
        <div><label className="label" htmlFor="consumer-purpose">What it is for</label><input id="consumer-purpose" className="input" maxLength={300} value={purpose} onChange={(e) => setPurpose(e.target.value)} /></div>
        <div>
          <p className="label">May call</p>
          {SCOPES.map((s) => (
            <label key={s.id} className="flex items-start gap-2 py-0.5 text-xs">
              <input type="checkbox" className="mt-0.5" checked={scopes.includes(s.id)} onChange={(e) => setScopes((x) => SCOPES.map((y) => y.id).filter((id) => (id === s.id ? e.target.checked : x.includes(id))))} />
              <span>{s.label}<span className="block text-2xs text-muted">{s.apis} · {s.header}</span></span>
            </label>
          ))}
          <p className="text-2xs text-muted">Each scope starts with the usual limits; change them on its card.</p>
        </div>
        {problem && title && <p className="field-error">{problem}</p>}
      </div>
    </Drawer>
  )
}
